import { IEventListItem } from '@gem/call/CallController/CallController.types';
import { Call } from '@gem/call/CallStore/Call';
import {
  PermissionsPersonalizedValue,
  PermissionsValue,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallEventTypes } from '@gem/call/constants';
import { t } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';

import {
  PERMISSIONS_GLOBAL_MEMBER_NOTIFICATIONS_TRANSLATIONS,
  PERMISSIONS_MEMBER_NOTIFICATIONS_TRANSLATIONS,
  PERMISSIONS_NOTIFICATIONS_TRANSLATIONS,
  PERMISSIONS_REQUEST_TRANSLATIONS,
  PERMISSIONS_REQUESTS_DECLINED_TRANSLATIONS,
} from './CallEventsService.constants';

export class CallEventsService {
  call: Call;

  constructor(params: { call: Call }) {
    this.call = params.call;
  }

  addEvent(data: IEventListItem): void {
    switch (data.type) {
      case CallEventTypes.ForceMute: {
        const initiator = this.call.members.getMemberById(data.initiatorId!);

        const user = this.call.members.getMemberById(data.userId!);

        if (!user || !initiator) {
          return;
        }

        const text = t('calls_force_mute_description', {
          0: initiator.displayName,
          1: user.displayName,
        });

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });

        break;
      }
      case CallEventTypes.Finished: {
        if (!data.userId) {
          return;
        }

        const member = this.call.members.getMemberById(data.userId);
        if (!member) {
          return;
        }
        const text = t('call_left_description', { 0: member.displayName });

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.Joined: {
        const member = this.call.members.getMemberById(data.userId!);
        if (!member) {
          return;
        }
        const text = t('calls_joined_description', { 0: member.displayName });

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.RaiseHand: {
        const user = this.call.members.getMemberById(data.userId!);
        if (!user) {
          return;
        }

        this.call.events.addEvent({
          type: data.type,
          displayName: user.displayName,
          avatarColorIndex: user.avatarColorIndex,
          avatarUrl: user.avatarUrl,
          timestamp: +dateService.now(),
          userId: data.userId!,
        });
        break;
      }
      case CallEventTypes.Added: {
        // GEM-33865: тост "добавлен в группу" выключен как некорректный,
        // оставляем только Joined
        break;
      }
      case CallEventTypes.Removed: {
        const initiator = this.call.members.getMemberById(data.initiatorId!);
        const user = this.call.members.getMemberById(data.userId!);
        if (!user || !initiator) {
          return;
        }
        const text = t('calls_user_removed_from_chat', {
          0: initiator.displayName,
          1: user.displayName,
        });

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.ForceFinish: {
        const initiator = this.call.members.getMemberById(data.initiatorId!);
        const user = this.call.members.getMemberById(data.userId!);
        if (!user || !initiator || user === initiator) {
          return;
        }
        const text = t('calls_user_removed_from_call', {
          0: initiator.displayName,
          1: user.displayName,
        });

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionsGlobalChanged: {
        if (!data.permissionType || !data.permissionValue) {
          return;
        }
        const translationTypeObject = PERMISSIONS_NOTIFICATIONS_TRANSLATIONS[data.permissionType];

        const translationValue = translationTypeObject
          ? translationTypeObject[data.permissionValue as PermissionsValue]
          : '';

        const text = t(translationValue);

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionsGlobalMemberChanged: {
        if (!data.permissionType || !data.permissionValue) {
          return;
        }
        const translationTypeObject =
          PERMISSIONS_GLOBAL_MEMBER_NOTIFICATIONS_TRANSLATIONS[data.permissionType];
        const translationValue = translationTypeObject
          ? translationTypeObject[data.permissionValue as PermissionsPersonalizedValue]
          : '';

        const text = t(translationValue);

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionsMemberChanged: {
        if (!data.permissionType) {
          return;
        }
        const translationTypeObject =
          PERMISSIONS_MEMBER_NOTIFICATIONS_TRANSLATIONS[data.permissionType];
        const translationValue = translationTypeObject
          ? translationTypeObject[data.permissionValue as PermissionsPersonalizedValue]
          : '';

        const text = t(translationValue);

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionsRequestDeclined: {
        if (!data.permissionType) {
          return;
        }
        const text = t(PERMISSIONS_REQUESTS_DECLINED_TRANSLATIONS[data.permissionType]);

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionRequestSent: {
        const text = t('call_permissions_request_sent');

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionRequestAlreadySent: {
        const text = t('call_permissions_request_already_exists');

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
      case CallEventTypes.PermissionsRequestCreated: {
        const user = this.call.members.getMemberById(data.userId!);

        if (!user || !data.requestType) {
          return;
        }

        const text = t(PERMISSIONS_REQUEST_TRANSLATIONS[data.requestType], { 0: user.displayName });

        this.call.events.addEvent({
          type: data.type,
          text,
          timestamp: +dateService.now(),
        });
        break;
      }
    }
  }

  deleteEvent(key: number): void {
    this.call.events.deleteEvent(key);
  }

  dispose(): void {
    this.call.events.dispose();
  }
}
